import type { Color, EvalSummary } from "../go/types";
import type { GradeLabel, MoveAnalysis, MoveGrade } from "./types";

// Point-loss thresholds (mover's perspective) for each grade. Tuned for SDK learners: small
// inaccuracies are forgiven, but giving up several points is flagged clearly. The winrate drop is
// used as a secondary trigger so that a swingy life-and-death blunder still grades as a blunder
// even when the raw score gap looks modest.
const POINT_THRESHOLDS: { label: GradeLabel; maxPointLoss: number }[] = [
  { label: "Great", maxPointLoss: 0.5 },
  { label: "Good", maxPointLoss: 1.5 },
  { label: "Inaccuracy", maxPointLoss: 3 },
  { label: "Mistake", maxPointLoss: 6 }
  // anything beyond is a "Blunder"
];

function labelFor(pointLoss: number, winrateDrop: number): GradeLabel {
  // Winrate cliff overrides the score-based bucket (e.g. a move that loses a key group).
  if (winrateDrop >= 0.25) return "Blunder";
  if (winrateDrop >= 0.12 && pointLoss < 6) return "Mistake";
  for (const { label, maxPointLoss } of POINT_THRESHOLDS) {
    if (pointLoss <= maxPointLoss) return label;
  }
  return "Blunder";
}

// Grade a played move by comparing it to the best move in the analysis of the position *before*
// it was played. `best` and `played` are both taken from the mover's perspective (higher score /
// winrate is better). `played` may be undefined if the move was outside the displayed candidates,
// in which case the caller should treat the result as a lower-bound estimate.
export function gradeMove(best: MoveAnalysis, played: MoveAnalysis | undefined): MoveGrade | null {
  if (best.scoreLead === undefined && best.winrate === undefined) {
    return null;
  }

  const playedScore = played?.scoreLead ?? best.scoreLead;
  const playedWin = played?.winrate ?? best.winrate;

  const pointLoss = Math.max(0, (best.scoreLead ?? 0) - (playedScore ?? 0));
  const winrateDrop = Math.max(0, (best.winrate ?? 0) - (playedWin ?? 0));

  return {
    label: labelFor(pointLoss, winrateDrop),
    pointLoss,
    winrateDrop,
    bestLabel: best.label,
    bestPv: best.pv
  };
}

// Grade a played move from the realized eval swing between the position before and after it. Used
// by game review, where we have each position's cached evaluation but not the candidate list. The
// loss is measured from the mover's perspective: how much their score / winrate fell.
export function gradeFromEvalSwing(before: EvalSummary, after: EvalSummary, mover: Color): MoveGrade {
  const sign = mover === "black" ? 1 : -1;
  const pointLoss = Math.max(0, sign * (before.scoreLeadBlack - after.scoreLeadBlack));
  const winrateDrop = Math.max(0, sign * (before.winrateBlack - after.winrateBlack));
  return {
    label: labelFor(pointLoss, winrateDrop),
    pointLoss,
    winrateDrop,
    bestLabel: before.bestLabel
  };
}
